import { useEffect, useState, type Ref } from 'react';

import { TextField } from '@/components/ui/text-field';

/**
 * A TEXT FIELD THAT WRITES ON BLUR AND ENTER, never per keystroke.
 *
 * For a field whose every change costs something real — a write to the experience, a save, a
 * name that other surfaces re-derive from. Typing "velocity" through a per-keystroke field is
 * eight writes and eight re-renders of everything reading the value, and a half-typed name is
 * briefly the name.
 *
 * It lived in the Answer Recall manager, which was its only caller until the chip itself grew
 * a rename. Two copies of "commit on blur and Enter" is how two surfaces come to disagree
 * about when a name is saved, so it moved here rather than being pasted.
 *
 * `value` stays the source of truth: the draft follows it whenever it changes underneath, so a
 * rename made somewhere else lands in a field nobody is typing in.
 */
export function CommitField({
  value, onCommit, ariaLabel, placeholder, variant = 'prose', onDraft, id, className, inputRef,
  hasError, describedBy,
}: {
  value: string;
  onCommit: (next: string) => void;
  ariaLabel: string;
  placeholder?: string;
  /** `literal` for content matched or parsed verbatim — a SMILES, where `l` against `1` matters. */
  variant?: 'literal' | 'prose';
  /**
   * The in-progress value, for a caller that must follow the typing even though the WRITE waits
   * for blur.
   *
   * The structure editor draws the molecule as it is typed while committing it once, which is
   * the whole point of committing late there: a SMILES retyped from scratch passes through
   * one-atom states, and writing those would prune the molecule's arrows on the way past.
   */
  onDraft?: (next: string) => void;
  /** For a field named by a real `<label htmlFor>` rather than by its `aria-label` alone. */
  id?: string;
  /**
   * The value in the box is wrong, which `TextField` paints and announces.
   *
   * Forwarded rather than reimplemented: the field already owns the TIMING — see
   * `useDelayedStickyError`, which waits for the typing to settle so a half-written value does
   * not flash red, then holds the mark until the fault actually goes away. A caller deciding
   * that for itself would be a second answer to a question already answered here.
   */
  hasError?: boolean;
  /** The element saying what is wrong, wired to the input for a reader. */
  describedBy?: string;
  className?: string;
  /**
   * For a caller that has to read or move the CARET.
   *
   * The reaction workbench's Build buttons insert a template where you are typing — a branch
   * puts its two brackets around the caret and leaves it between them — and "where you are
   * typing" is a question only the input can answer.
   */
  inputRef?: Ref<HTMLInputElement>;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <TextField
      variant={variant}
      id={id}
      className={className}
      ariaLabel={ariaLabel}
      placeholder={placeholder}
      inputRef={inputRef}
      hasError={hasError}
      describedBy={describedBy}
      value={draft}
      onChange={(next) => { setDraft(next); onDraft?.(next); }}
      onBlur={() => { if (draft !== value) onCommit(draft); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
      }}
    />
  );
}
